/**
 * Chat composer: auto-growing textarea + attachment upload + send.
 *
 * - Enter sends, Shift+Enter inserts a newline.
 * - Attachments are uploaded up-front via the `getUploadUrl` callable (which
 *   returns a signed URL) and a direct `PUT`; the resulting `Attachment`
 *   descriptors are handed to `onSend` alongside the text.
 * - `sending` (true while the active conversation has a turn in flight — see
 *   `ChatView#composerBusy`) disables the send/attach buttons.
 * - `onSend` renders the user's message optimistically and never rejects (a
 *   failed send surfaces inline on the optimistic bubble with retry/discard —
 *   see `lib/optimisticMessages.ts`); the restore-on-failure below is a
 *   defensive fallback for any other unexpected throw.
 */
import type { Attachment } from '@agente/shared'
import { MAX_UPLOAD_BYTES } from '@agente/shared'
import { ArrowUp, FileText, Loader, Mic, Paperclip, X } from 'lucide-react'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { getUploadUrl } from '@/lib/callables'
import { cn } from '@/lib/utils'

interface PendingAttachment extends Attachment {
  localId: string
}

export interface ComposerProps {
  onSend: (text: string, attachments: Attachment[]) => Promise<void>
  disabled?: boolean
  sending?: boolean
  /** Abre a conversa por voz (renderizado só com VITE_VOICE_ENABLED=true). */
  onVoiceClick?: () => void
  /**
   * Rascunho para pré-preencher o campo (ex.: encaminhar um lembrete ao chat).
   * Só é aplicado quando o campo está vazio, para nunca sobrescrever o que o
   * usuário já digitou. Aplicado uma vez por valor distinto.
   */
  initialDraft?: string
}

/** Feature flag da conversa por voz (baked pelo Vite em build time). */
const VOICE_ENABLED = import.meta.env.VITE_VOICE_ENABLED === 'true'

export function Composer({ onSend, disabled, sending, onVoiceClick, initialDraft }: ComposerProps) {
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canSend = (text.trim().length > 0 || attachments.length > 0) && !sending && !uploading

  function autoGrow() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }

  // Pré-preenche o rascunho encaminhado (ex.: de um lembrete) apenas quando o
  // campo está vazio, para nunca sobrescrever o que o usuário já digitou. Roda
  // a cada valor distinto de initialDraft (o pai limpa o search param após montar
  // o rascunho, então o valor não se repete indevidamente).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagir só a initialDraft; text é lido, não observado
  useEffect(() => {
    if (!initialDraft) return
    setText((current) => (current.length === 0 ? initialDraft : current))
    requestAnimationFrame(autoGrow)
  }, [initialDraft])

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_UPLOAD_BYTES) {
          toast.error('Arquivo muito grande', {
            description: `"${file.name}" excede o limite de 25 MB.`,
          })
          continue
        }
        const { data } = await getUploadUrl({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
        })
        const res = await fetch(data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        })
        if (!res.ok) {
          throw new Error(`Falha no upload de "${file.name}" (${res.status}).`)
        }
        setAttachments((prev) => [
          ...prev,
          {
            localId: crypto.randomUUID(),
            name: file.name,
            storagePath: data.storagePath,
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
          },
        ])
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível anexar o arquivo', { description: message })
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function removeAttachment(localId: string) {
    setAttachments((prev) => prev.filter((a) => a.localId !== localId))
  }

  async function submit() {
    if (!canSend) return
    const outgoingText = text.trim()
    const outgoing = attachments.map(({ localId: _localId, ...rest }) => rest)
    // Optimistically clear so the composer feels responsive; restore on failure.
    setText('')
    setAttachments([])
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    try {
      await onSend(outgoingText, outgoing)
    } catch {
      setText(outgoingText)
      setAttachments(attachments)
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
  }

  return (
    <div className="border-border bg-background rounded-2xl border p-2 shadow-sm">
      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5 px-1">
          {attachments.map((att) => (
            <span
              key={att.localId}
              className="bg-muted flex max-w-[14rem] items-center gap-1.5 rounded-md py-1 pr-1 pl-2 text-xs"
            >
              <FileText className="text-muted-foreground size-3.5 shrink-0" />
              <span className="truncate">{att.name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(att.localId)}
                className="hover:bg-background rounded p-0.5"
                aria-label={`Remover ${att.name}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-1.5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          disabled={disabled || uploading || sending}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Anexar arquivo"
        >
          {uploading ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <Paperclip className="size-4" />
          )}
        </Button>

        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            autoGrow()
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          placeholder="Escreva para o Agente Executivo…"
          aria-label="Mensagem"
          className={cn(
            'max-h-[200px] min-h-9 flex-1 resize-none border-0 bg-transparent px-1 py-1.5 shadow-none',
            'focus-visible:ring-0',
          )}
        />

        {VOICE_ENABLED && onVoiceClick ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            disabled={disabled || sending}
            onClick={onVoiceClick}
            aria-label="Conversar por voz"
          >
            <Mic className="size-4" />
          </Button>
        ) : null}

        <Button
          type="button"
          size="icon"
          className="shrink-0 rounded-full shadow-sm"
          disabled={!canSend}
          onClick={() => void submit()}
          aria-label="Enviar mensagem"
        >
          {sending ? <Loader className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
        </Button>
      </div>
    </div>
  )
}
